import { getStats } from './StatsDB';
import { getConstants } from './ConstantsDB';
import { getAsteroids } from './AsteroidsDB';
import { getGameAsteroids } from './GameAsteroidsDB';
import { getUpgrades } from './UpgradesDB';
import { logger } from '@/engine/logging';
import { getCrafts } from './CraftDB';
import { getGameContent } from './GameContentDB';
import { getGameWorld } from './GameWorldDB';
import { getSectors } from './SectorsDB';

/**
 * Initializes all game databases in the correct order.
 * This ensures that dependencies between databases are respected.
 */
export function initializeGameDatabases(): void {
  logger.info('💾 Initializing game databases...', { source: 'GameDatabases' });

  // Tier 1: Core data with no dependencies
  getConstants().init();
  getAsteroids().init(); // Base asteroid configs
  getUpgrades().init();

  // Tier 2: Databases that depend on core data
  getStats().init(); // Depends on Constants and Upgrades

  // Tier 3: High-level aggregated databases
  getGameAsteroids().init(); // Combines multiple asteroid-related data sources

  // Tier 4: Other major databases
  getCrafts().init();
  getGameContent().init();
  getGameWorld().init();
  getSectors().init();

  logger.info('✅ Game databases initialized successfully', { source: 'GameDatabases' });
}

// Core databases
export { ConstantsDB, Constants } from './ConstantsDB';
export type { ConstantKey } from './ConstantsDB';

export { StatsDB, Stats } from './StatsDB';
export type { GameStats } from './StatsDB';

export { Formulas } from './formulas';

export { UpgradesDB, Upgrades } from './UpgradesDB';
export type { UpgradeId, UpgradeCost, UpgradePurchaseInfo } from './UpgradesDB';

export { CraftDB, Crafts } from './CraftDB';
export type {
  CraftId as CraftIdType,
  CraftCost,
  CraftInfo as CraftInfoType,
  ActiveCraft,
} from './CraftDB';

// Content databases
export { GameContentDB, GameContent } from './GameContentDB';
export type {
  ItemId,
  CraftId,
  LootPoolId,
  ItemInfo,
  CraftInfo,
  LootEntryInfo,
  ItemConfig,
  CraftConfig,
  LootPoolConfig,
  LootPoolEntryConfig,
} from './GameContentDB';

export { GameWorldDB, GameWorld } from './GameWorldDB';
export type {
  GalaxyId,
  SectorId,
  GalaxyInfo,
  SectorInfo,
  GalaxyConfig,
  SectorConfig,
} from './GameWorldDB';

export { GameAsteroidsDB, GameAsteroids } from './GameAsteroidsDB';
export type {
  AsteroidId,
  AsteroidSizeId,
  AsteroidMixId,
  AsteroidInfo,
  AsteroidSizeInfo,
  AsteroidMixInfo,
  AsteroidConfig,
  AsteroidSizeConfig,
  AsteroidMixConfig,
  AsteroidMixEntryConfig,
} from './GameAsteroidsDB';

export { SectorsDB, Sectors } from './SectorsDB';
export type { SectorProgress } from './SectorsDB';

// Loot database
export { LootDB, lootDB } from './LootDB';
export type { LootEntry, LootTable } from './LootDB';
export { ItemsDB, itemsDB } from './ItemsDB';
export type { ItemInfo as GameItemInfo } from './ItemsDB';

// Legacy exports (for backward compatibility)
export { AsteroidsDB, Asteroids } from './AsteroidsDB';
export type { AsteroidKey, AsteroidConfig as LegacyAsteroidConfig } from './AsteroidsDB';
